// ProjectsSection — top-level grouping in the SessionsRail that lists the
// user's projects. Framework instances (which are also long-running
// containers) live in the same visual tier per designer Q7 — V1 ships with
// just projects, framework-instance rendering here lands with a small
// follow-up MR once we have the client-side framework lookup.
//
// Interaction (per designer Q8-Q13):
// - Collapsed list of project names with session counts.
// - Click a project → filters the date-grouped sessions below (parent-owned).
// - "+ New project" button opens an inline CreateProjectDialog.
// - Each row has a kebab (⋯) that opens the ArchiveOrDeleteConfirm panel.

import { useEffect, useState } from 'react';
import type { CoachProject } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import {
  createCoachProject,
  deleteCoachProject,
  listCoachProjects,
  patchCoachProject,
} from '../api/endpoints.js';
import styles from './Coach.module.css';

export interface ProjectsSectionProps {
  activeProjectId: string | null;
  onSelectProject: (projectId: string | null) => void;
  /** Fired whenever the projects list changes (create/rename/archive/delete).
   *  Parent can use this to refresh anything that depends on the list. */
  onProjectsChanged?: () => void;
}

export function ProjectsSection({
  activeProjectId,
  onSelectProject,
  onProjectsChanged,
}: ProjectsSectionProps) {
  const { t, tt } = useI18n();
  const [projects, setProjects] = useState<CoachProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState<CoachProject | null>(null);

  async function reload() {
    const result = await listCoachProjects();
    if (result.ok) setProjects([...result.data.projects]);
    setLoading(false);
  }

  useEffect(() => {
    void reload();
  }, []);

  async function onCreated(name: string, description: string) {
    const result = await createCoachProject({ name, ...(description ? { description } : {}) });
    if (result.ok) {
      setProjects((prev) => [result.data.project, ...prev]);
      setCreating(false);
      onProjectsChanged?.();
    }
  }

  async function onArchive(project: CoachProject) {
    const result = await patchCoachProject(project.id, { archived: true });
    if (result.ok) {
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      if (activeProjectId === project.id) onSelectProject(null);
      setManaging(null);
      onProjectsChanged?.();
    }
  }

  async function onDelete(project: CoachProject) {
    const result = await deleteCoachProject(project.id);
    if (result.ok) {
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      if (activeProjectId === project.id) onSelectProject(null);
      setManaging(null);
      onProjectsChanged?.();
    }
  }

  return (
    <div className={styles.projectsSection}>
      <div className={styles.projectsHeader}>
        <span className={styles.projectsSectionLabel}>{t('projects.section')}</span>
        <button
          type="button"
          className={styles.projectsNewBtn}
          onClick={() => setCreating(true)}
        >
          {t('projects.new')}
        </button>
      </div>

      {!loading && projects.length === 0 && (
        <div className={styles.projectsEmpty}>{t('projects.empty')}</div>
      )}

      {projects.map((p) => (
        <button
          type="button"
          key={p.id}
          className={`${styles.projectRow} ${activeProjectId === p.id ? styles.projectRowActive : ''}`}
          onClick={() => onSelectProject(activeProjectId === p.id ? null : p.id)}
        >
          <span className={styles.projectRowName}>{p.name}</span>
          <span className={styles.projectRowCount}>{p.sessionCount}</span>
          <button
            type="button"
            className={styles.projectRowManage}
            onClick={(e) => {
              e.stopPropagation();
              setManaging(p);
            }}
            aria-label={tt('projects.manageTitle', { project: p.name })}
          >
            ⋯
          </button>
        </button>
      ))}

      {creating && (
        <CreateProjectDialog
          onCancel={() => setCreating(false)}
          onCreate={(name, description) => void onCreated(name, description)}
        />
      )}

      {managing && (
        <ArchiveOrDeleteConfirm
          project={managing}
          onCancel={() => setManaging(null)}
          onArchive={() => void onArchive(managing)}
          onDelete={() => void onDelete(managing)}
        />
      )}
    </div>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────

function CreateProjectDialog({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (name: string, description: string) => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const canSave = name.trim().length > 0;

  return (
    <div className={styles.projectDialog}>
      <div className={styles.projectDialogTitle}>{t('projects.createTitle')}</div>
      <div className={styles.projectDialogField}>
        <label className={styles.projectDialogLabel}>{t('projects.createNameLabel')}</label>
        <input
          type="text"
          className={styles.projectDialogInput}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>
      <div className={styles.projectDialogField}>
        <label className={styles.projectDialogLabel}>{t('projects.createDescLabel')}</label>
        <input
          type="text"
          className={styles.projectDialogInput}
          placeholder={t('projects.desc.placeholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className={styles.projectDialogActions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnBrand}`}
          onClick={() => canSave && onCreate(name.trim(), description.trim())}
          disabled={!canSave}
        >
          {t('projects.createSave')}
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onCancel}>
          {t('projects.cancel')}
        </button>
      </div>
    </div>
  );
}

// ─── Archive-or-Delete confirm panel ──────────────────────────────────────

function ArchiveOrDeleteConfirm({
  project,
  onCancel,
  onArchive,
  onDelete,
}: {
  project: CoachProject;
  onCancel: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const { t, tt } = useI18n();
  const n = String(project.sessionCount);
  return (
    <div className={styles.projectConfirm}>
      <div className={styles.projectConfirmTitle}>
        {tt('projects.manageTitle', { project: project.name })}
      </div>
      <div className={styles.projectConfirmChoice}>
        <div>
          <div className={styles.projectConfirmChoiceLabel}>{t('projects.archive')}</div>
          <div className={styles.projectConfirmChoiceDesc}>
            {tt('projects.archiveDesc', { n })}
          </div>
        </div>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnGhost}`}
          onClick={onArchive}
        >
          {t('projects.archiveGo')}
        </button>
      </div>
      <div className={styles.projectConfirmChoice}>
        <div>
          <div className={styles.projectConfirmChoiceLabel}>{t('projects.delete')}</div>
          <div className={styles.projectConfirmChoiceDesc}>
            {tt('projects.deleteDesc', { n })}
          </div>
        </div>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnDanger}`}
          onClick={onDelete}
        >
          {t('projects.delete')}
        </button>
      </div>
      <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onCancel}>
        {t('projects.cancel')}
      </button>
    </div>
  );
}
